import {useTranslation} from '@aboutyou/i18n';
import {getAdjustedCdnUrl} from '@aboutyou/utils';
import React from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES} from '../../../constants/typography';
import type {ImageType} from '../../../types/Image';
import {Button} from '../../atoms/Buttons/Button';
import {ImageBubble} from '../../atoms/ImageBubble/ImageBubble';
import {Text} from '../../atoms/Text/Text';
import {UnstyledLink} from '../../atoms/UnstyledLink/UnstyledLink';

export interface ProfileCardProps {
  to?: string;
  image?: ImageType;
  headline: string;
  subline?: string;
  onClick?: (ev: React.MouseEvent) => void;
  followButton: React.ReactNode | null;
}

export const ProfileCard: React.FC<ProfileCardProps> = ({
  image,
  to,
  headline,
  subline,
  onClick,
  followButton,
}) => {
  return (
    <Container data-testid="profileCard">
      <ProfileDetails
        to={to}
        image={image}
        headline={headline}
        subline={subline}
        onClick={onClick}
      />
      {followButton}
    </Container>
  );
};

const ProfileDetails = ({
  to,
  image,
  headline,
  subline,
  onClick,
}: Pick<
  ProfileCardProps,
  'image' | 'headline' | 'subline' | 'to' | 'onClick'
>) => {
  return (
    <ProfileDetailsContainer
      as={to ? UnstyledLink : 'section'}
      to={to}
      onClick={onClick}
      data-testid="profileDetails"
    >
      {image && (
        <OuterRoundedContainer>
          <ImageBubble
            loading="lazy"
            importance="low"
            src={getAdjustedCdnUrl(image.url, {
              width: 80,
              height: 80,
            })}
            alt={image.description}
          />
        </OuterRoundedContainer>
      )}
      <ProfileInfo>
        <ProfileHeadline data-testid="profileHeadline" size="px14">
          {headline}
        </ProfileHeadline>
        {subline && (
          <ProfileSubline data-testid="profileSubline" size="px12">
            {subline}
          </ProfileSubline>
        )}
      </ProfileInfo>
    </ProfileDetailsContainer>
  );
};

export const ProfileFollowButton = ({
  isFollowed,
  onFollow,
}: {
  isFollowed: boolean;
  onFollow: () => void;
}) => {
  const {t} = useTranslation();

  const handleFollowClick = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onFollow();
  };

  return (
    <FollowButton
      data-testid="followButton"
      onClick={handleFollowClick}
      $isActive={isFollowed}
      variant="secondary"
      size="fit-width"
    >
      {isFollowed ? t('You follow') : t('Follow')}
    </FollowButton>
  );
};

const ProfileDetailsContainer = styled.section<{
  to?: string;
}>`
  display: flex;
  overflow: hidden;
`;

const FollowButton = styled(Button)<{$isActive?: boolean}>`
  max-height: 32px;
  padding: 0 0.875rem;
  font-size: ${FONT_SIZES.px12};
  --border-color: ${({$isActive}) =>
    $isActive ? `${COLORS['Fill.Black']}` : `${COLORS['Outline.Grey']}`};
  flex-shrink: 0;
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  p {
    margin: 0;
  }
  flex: 1;
  justify-content: center;
  margin-right: 0.25rem;
`;

const ProfileHeadline = styled(Text)`
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  color: ${COLORS.Black};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProfileSubline = styled(Text)`
  font-weight: 500;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  color: ${COLORS.Grey1};
`;

const OuterRoundedContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 42px;
  width: 42px;
  margin-right: 15px;
  flex-shrink: 0;
`;

const Container = styled.div`
  display: flex;
  padding: 18px 10px;
  justify-content: space-between;
  align-items: center;
  position: relative;
`;
